/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { ExternalLinkIcon } from 'foundations-assets/icons/components/ExternalLinkIcon';
import { Button } from 'foundations-components/transitional/components/Button';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { Flex } from 'foundations-components/components/Flex';
import { H1 } from 'foundations-components/transitional/components/H1';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { View } from 'foundations-components/components/View';

// @ts-expect-error Could not find a declaration file for module
import { setUserGuideSettingsAndReturnUrl } from '../../actions/GettingStartedActions';
import { currencies, hubPricingFeaturesI18Nkeys } from '../../constants/AcademyToSoftwarePricingData';
import { onboardingSettingsExperienceMap, onboardingSettingsHubMap } from '../../constants/GettingStatedConsts';
import { useDiscounts } from '../../features/monetization/monetizationQueries';
// @ts-expect-error Could not find a declaration file for module
import { getAccountsProductsByPortal } from '../../selectors/accountsSelectors';
// @ts-expect-error Could not find a declaration file for module
import { getHubPricingData } from '../../selectors/productsSelectors';
import { getPricingPageUrl } from '../../utils/RoutingUtils';
import { getHighestHubPlan, getAnnualDiscountPercentage, getPriceForProduct } from '../../utils/pricingUtils';
import UsageTracker from '../../utils/UsageTracker';
import PricingCard from './PricingCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ModalPricingSection = ({
  hub,
  onCTAClick,
  priceLoading
}) => {
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const {
    discounts
  } = useDiscounts();
  const {
    isMobile
  } = useMediaQuery();
  const dispatch = useDispatch();
  const portalId = useSelector(state => {
    var _state$auth;
    return state === null || state === void 0 || (_state$auth = state.auth) === null || _state$auth === void 0 || (_state$auth = _state$auth.portal) === null || _state$auth === void 0 ? void 0 : _state$auth.portal_id;
  });
  const ownedProducts = useSelector(getAccountsProductsByPortal);
  const hubPricingData = useSelector(getHubPricingData);
  const pricingCards = useMemo(() => Object.keys(hubPricingFeaturesI18Nkeys[hub]).map(pricingLevelKey => {
    const level = pricingLevelKey;
    const annualDiscountPercentage = getAnnualDiscountPercentage({
      discounts: discounts || undefined,
      hub,
      pricingLevel: level
    }) || 0;
    const price = getPriceForProduct({
      hubPricingData,
      hub,
      pricingLevel: level,
      selectedCurrency,
      annualDiscountPercentage
    });
    const features = hubPricingFeaturesI18Nkeys[hub][level].map(feature => /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: `learningCenter.softwareDesirability.modal.hubs.${hub}.pricingFeatures.${level}.${feature}`
    }, feature));
    return /*#__PURE__*/_jsx(PricingCard, {
      currency: selectedCurrency,
      features: features,
      isCurrentPlan: ownedProducts && getHighestHubPlan(ownedProducts, hub) === level,
      level: level,
      price: price !== null && price !== void 0 ? price : 0,
      priceLoading: priceLoading
    }, level);
  }), [hub, hubPricingData, ownedProducts, priceLoading, selectedCurrency, discounts]);
  const currentPlan = ownedProducts ? getHighestHubPlan(ownedProducts, hub) : 'free';
  const isFreeOrStarter = ['free', 'starter'].includes(currentPlan);
  const isProfessionalOrEnterprise = ['professional', 'enterprise'].includes(currentPlan);
  const PricingCardGridProps = {
    alignItems: 'end',
    gap: '400',
    gridTemplateColumns: !isMobile ? 'repeat(2, 250px)' : '',
    justifyContent: 'center'
  };
  const onClickFeaturesButton = () => {
    UsageTracker.track('interaction', {
      action: 'academy-to-software-modal-features-pricing-click',
      hub,
      ownedPlan: currentPlan,
      section: 'pricing'
    });
    onCTAClick();
  };
  const onClickUserGuideButton = () => {
    UsageTracker.track('interaction', {
      action: 'academy-to-software-modal-user-guide-click',
      hub,
      ownedPlan: currentPlan,
      section: 'pricing'
    });
    dispatch(setUserGuideSettingsAndReturnUrl(onboardingSettingsHubMap[hub.toUpperCase()], onboardingSettingsExperienceMap.NO_EXPERIENCE)).then(redirectUrl => {
      onCTAClick();
      window.open(redirectUrl, '_blank');
    });
  };
  return /*#__PURE__*/_jsxs(View, {
    backgroundColor: 'fill-field-default',
    padding: isMobile ? '400' : '1000',
    paddingBlock: "1000",
    textAlign: "center",
    children: [/*#__PURE__*/_jsx(H1, {
      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "learningCenter.softwareDesirability.modal.pricingSection.heading",
        options: {
          hubName: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `learningCenter.softwareDesirability.modal.hubs.${hub}.name`
          })
        }
      })
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.softwareDesirability.modal.pricingSection.subheading"
    }), /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      justifyContent: "center",
      marginBlockStart: "800",
      children: /*#__PURE__*/_jsxs(CardGrid, {
        display: "grid",
        gap: "400",
        gridTemplateColumns: !isMobile ? 'repeat(auto-fit, 516px)' : 'unset',
        inlineSize: "100%",
        justifyContent: "center",
        position: "relative",
        children: [/*#__PURE__*/_jsx(Flex, {
          inlineSize: "100%",
          insetBlockStart: !isMobile ? '-200' : '0',
          insetInlineEnd: !isMobile && isFreeOrStarter ? '1200' : 'unset',
          insetInlineStart: !isMobile && isProfessionalOrEnterprise ? '1200' : 'unset',
          justifyContent: isFreeOrStarter ? 'end' : 'start',
          position: !isMobile ? 'absolute' : 'relative',
          children: /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            flexGrow: 0,
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.softwareDesirability.modal.pricingSection.currency"
            }), /*#__PURE__*/_jsx(Dropdown, {
              buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
                message: `learningCenter.softwareDesirability.modal.pricingSection.currencies.${selectedCurrency}`
              }),
              buttonUse: "transparent",
              closeOnMenuClick: true,
              "data-test-id": "academy-to-software-desirability-modal-currency-dropdown",
              children: /*#__PURE__*/_jsx(List, {
                wrapChildren: true,
                children: currencies.map(currency => /*#__PURE__*/_jsx(ListItem, {
                  children: /*#__PURE__*/_jsx(Button, {
                    "data-test-id": `academy-to-software-desirability-modal-currency-${currency}`,
                    onClick: () => {
                      UsageTracker.track('interaction', {
                        action: 'academy-to-software-modal-currency-changed',
                        hub,
                        ownedPlan: currentPlan,
                        currency
                      });
                      setSelectedCurrency(currency);
                    },
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: `learningCenter.softwareDesirability.modal.pricingSection.currencies.${currency}`
                    })
                  })
                }, currency))
              })
            })]
          })
        }), /*#__PURE__*/_jsx(CardGrid, Object.assign({}, PricingCardGridProps, {
          children: pricingCards.slice(0, 2)
        })), /*#__PURE__*/_jsx(CardGrid, Object.assign({}, PricingCardGridProps, {
          children: pricingCards.slice(2)
        }))]
      })
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '400' : '300',
      justifyContent: "center",
      marginBlockStart: "400",
      children: [/*#__PURE__*/_jsxs(Button, {
        "data-test-id": "academy-to-software-desirability-modal-pricing-section-user-guide-button",
        external: true,
        onClick: onClickUserGuideButton,
        paddingBlock: "400",
        use: "primary",
        children: [/*#__PURE__*/_jsx(FormattedReactMessage, {
          message: `learningCenter.softwareDesirability.modal.${currentPlan === 'free' ? 'primaryCTA' : 'primaryCTAPaid'}`,
          options: {
            hubName: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `learningCenter.softwareDesirability.modal.hubs.${hub}.name`
            })
          }
        }), /*#__PURE__*/_jsx(ExternalLinkIcon, {
          size: "200"
        })]
      }), /*#__PURE__*/_jsx(Button, {
        "data-test-id": "academy-to-software-desirability-modal-pricing-section-features-button",
        external: true,
        href: getPricingPageUrl(portalId, hub),
        onClick: onClickFeaturesButton,
        paddingBlock: "400",
        target: "_blank",
        use: "secondary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.softwareDesirability.modal.secondaryCTA"
        })
      })]
    })]
  });
};
export default ModalPricingSection;